import React, { useRef, forwardRef, useImperativeHandle } from "react";
import {
  Cropper as AdvancedCropper,
  ImageRestriction,
  RectangleStencil,
  CircleStencil,
} from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";
import "./Icons/Cropper.css";

import {
    fitStencilToImage,
    zoomStencil,
    defaultSize,
    resizeCoordinates,
    stencilConstraints,
    transformImage,
} from "advanced-cropper/showcase/mobile";

import CropperWrapper from "./CropperWrapper";

const Cropper = forwardRef(({
  src,
  className = "",
  stencilProps = {},
  navigation = true,
  navigationProps = {},
  imageRestriction = ImageRestriction.stencil,
  onUpdate,
  aspectRatio,
  stencilType = "rectangle",
  resetVersion = 0,
}, ref) => {
  const cropperRef = useRef(null);
  const callCropperMethod = (methodName, args) => {
    if (!cropperRef.current || typeof cropperRef.current[methodName] !== "function") {
      return undefined;
    }
    return cropperRef.current[methodName](...args);
  };

  useImperativeHandle(ref, () => ({
    getCanvas: (...args) => callCropperMethod("getCanvas", args),
    getCoordinates: (...args) => callCropperMethod("getCoordinates", args),
    getTransforms: (...args) => callCropperMethod("getTransforms", args),
    rotateImage: (...args) => callCropperMethod("rotateImage", args),
    flipImage: (...args) => callCropperMethod("flipImage", args),
    transformImageEnd: (...args) => callCropperMethod("transformImageEnd", args),
    reset: (...args) => callCropperMethod("reset", args),
    refresh: (...args) => callCropperMethod("refresh", args),
  }));

  const stencilComponent = stencilType === "circle" ? CircleStencil : RectangleStencil;

  return (
    <AdvancedCropper
      key={`stencil-${stencilType}-reset-${resetVersion}`}
      src={src}
      ref={cropperRef}
      className={`rmc-cropper ${className}`}
      onUpdate={onUpdate}

      // ✅ Stencil settings
      stencilComponent={stencilComponent}
      stencilConstraints={stencilConstraints}
      stencilProps={{
        grid: true,
        movable: false,
        ...(typeof aspectRatio === "number" && !Number.isNaN(aspectRatio)
          ? { aspectRatio }
          : {}),
        ...stencilProps,
      }}

            // ✅ Wrapper (your simplified one)
            wrapperComponent={CropperWrapper}
            wrapperProps={{
            navigation,
            navigationProps,
            }}

      // ✅ Behavior
      imageRestriction={ImageRestriction.none}
      defaultSize={defaultSize}
      transformImageAlgorithm={transformImage}
      transitions={false}

            // ✅ Mobile-like behavior
            postProcess={
            imageRestriction === ImageRestriction.none
                ? zoomStencil
                : [fitStencilToImage, zoomStencil]
            }

      resizeCoordinatesAlgorithm={
        imageRestriction === ImageRestriction.none
          ? undefined
          : resizeCoordinates
      }
    />
  );
});

export default Cropper;